import React from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Box, 
  Chip,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import { 
  AddShoppingCart as AddToCartIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import productService from '../../services/productService';

// Styled components for better visuals
const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
    '& .MuiCardMedia-root': {
      opacity: 0.9,
    },
    '& .product-actions': {
      opacity: 1,
    },
  },
}));

const ProductMedia = styled(CardMedia)(({ theme }) => ({
  paddingTop: '75%', // 4:3 aspect ratio
  position: 'relative',
  backgroundColor: theme.palette.grey[100],
}));

const ProductActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  opacity: 0,
  transition: 'opacity 0.2s',
  zIndex: 2,
}));

const StockIndicator = styled(Chip)(({ theme, stock }) => ({
  position: 'absolute',
  bottom: 8,
  left: 8,
  backgroundColor: 
    stock <= 0 ? theme.palette.error.main :
    stock < 10 ? theme.palette.warning.main :
    theme.palette.success.main,
  color: theme.palette.common.white,
  zIndex: 2,
}));

/**
 * Component to display products in a grid with cards
 * @param {Object} props Component props
 * @param {Array} props.products List of products to display
 * @param {Function} props.onAddToCart Callback when adding a product to cart
 * @returns {JSX.Element} Grid of product cards
 */
const POSProductList = ({ products, onAddToCart }) => {
  const theme = useTheme();

  // Function to get product image URL
  const getProductImage = (product) => {
    if (product.imageUrl) {
      return productService.getProductImageUrl(product.imageUrl);
    }
    return '/placeholder-product.png';
  };

  // Format currency
  const formatCurrency = (amount) => {
    // Check if amount is a number and valid
    if (amount === undefined || amount === null || isNaN(Number(amount))) {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Handler for adding to cart
  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    onAddToCart(product);
  };

  return (
    <Grid 
      container 
      spacing={2} 
      sx={{ 
        p: 2,
        // Add custom scrollbar styles
        maxHeight: 'calc(100vh - 160px)', // Add a max height to force scrollbar
        overflowY: 'auto', // Enable vertical scrolling
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }
        },
        scrollbarWidth: 'thin', // For Firefox
        scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)' // For Firefox
      }}
    >
      {products.length === 0 ? (
        <Box sx={{ 
          width: '100%', 
          textAlign: 'center', 
          py: 8,
          px: 2 
        }}>
          <InventoryIcon sx={{ fontSize: 60, color: theme.palette.grey[300] }} />
          <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
            No products found. Try adjusting your search or category filter.
          </Typography>
        </Box>
      ) : (
        products.map(product => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={product.id}>
            <ProductCard
              onClick={() => onAddToCart(product)}
              elevation={1}
            >
              <ProductMedia
                image={getProductImage(product)}
                title={product.name}
              >
                <ProductActions className="product-actions">
                  <Tooltip title="Add to cart">
                    <IconButton
                      onClick={(e) => handleAddToCart(e, product)}
                      size="small"
                      sx={{ 
                        bgcolor: 'background.paper', 
                        boxShadow: 1,
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText'
                        }
                      }}
                    >
                      <AddToCartIcon />
                    </IconButton>
                  </Tooltip>
                </ProductActions>
                
                <StockIndicator 
                  label={
                    product.stockQuantity <= 0 ? 'Out of stock' :
                    `${product.quantity || 0} in stock`
                  }
                  size="small"
                  stock={product.quantity || 0}
                />
              </ProductMedia>
              
              <CardContent sx={{ flexGrow: 1, p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    component="h3"
                    noWrap
                    title={product.name}
                    sx={{ mb: 0.5, fontWeight: 'medium' }}
                  >
                    {product.name}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    noWrap
                    title={product.description || 'No description'}
                    sx={{ fontSize: '0.75rem', mb: 1 }}
                  >
                    {product.description || 'No description'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(product.sellingPrice || product.salePrice)}
                  </Typography>
                  
                  {product.category && product.category.name && (
                    <Chip
                      label={product.category.name}
                      size="small"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.65rem',
                        backgroundColor: theme.palette.grey[200]
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </ProductCard>
          </Grid>
        ))
      )}
    </Grid>
  );
};

export default POSProductList; 